import { Fragment, useCallback } from 'react';
import Chip from '@material-ui/core/Chip';
import IconButton from '@material-ui/core/IconButton';
import CachedIcon from '@material-ui/icons/Cached';
import EditIcon from '@material-ui/icons/Edit';
import DocsIcon from '@material-ui/icons/Description';
import { Grid } from '@backstage/ui';
import {
  Link,
  HeaderIconLinkRow,
  type IconLinkVerticalProps,
} from '@backstage/core-components';
import { AboutContent, AboutField } from '@backstage/plugin-catalog';
import {
  useEntity,
  catalogApiRef,
  EntityInfoCard,
  getEntitySourceLocation,
} from '@backstage/plugin-catalog-react';
import { useEntityPermission } from '@backstage/plugin-catalog-react/alpha';
import { catalogEntityRefreshPermission } from '@backstage/plugin-catalog-common/alpha';
import {
  useApi,
  alertApiRef,
  errorApiRef,
  useRouteRef,
} from '@backstage/core-plugin-api';
import {
  scmIntegrationsApiRef,
  ScmIntegrationIcon,
} from '@backstage/integration-react';
import {
  ANNOTATION_EDIT_URL,
  ANNOTATION_LOCATION,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { techdocsPlugin } from '@backstage/plugin-techdocs';
import { buildTechDocsURL } from '@backstage/plugin-techdocs-react';
import {
  TECHDOCS_ANNOTATION,
  TECHDOCS_EXTERNAL_ANNOTATION,
} from '@backstage/plugin-techdocs-common';
import {
  getLocalDev,
  localDevColor,
  localDevLabel,
  localDevValueLabel,
  LOCAL_DEV_FIELDS,
} from './localDev';

/**
 * Renders the two `spec.localDev` fields as {@link AboutField}s inside the
 * same `Grid.Root` layout Backstage's built-in AboutContent uses, so they
 * line up with Owner / System / Type / Lifecycle / Tags. Only shown for
 * Component entities (where `spec.localDev` lives).
 */
function LocalDevFields() {
  const { entity } = useEntity();
  if (entity.kind.toLocaleLowerCase('en-US') !== 'component') {
    return null;
  }

  // Same responsive column config as AboutContent (plugin-catalog).
  const columns = { initial: '1', sm: '2', lg: '3' } as const;

  return (
    <Grid.Root columns={columns} gap="3">
      {LOCAL_DEV_FIELDS.map(field => {
        const value = getLocalDev(entity, field);
        const label = localDevLabel(field);
        if (!value) {
          return <AboutField key={field} label={label} value="unknown" />;
        }
        return (
          <AboutField key={field} label={label}>
            <Chip
              size="small"
              label={localDevValueLabel(field, value)}
              style={{
                backgroundColor: localDevColor(field, value),
                color: '#fff',
              }}
            />
          </AboutField>
        );
      })}
    </Grid.Root>
  );
}

/** Icon-link subheader mirroring the built-in AboutCard (View Source / TechDocs). */
function AboutSubheader() {
  const { entity } = useEntity();
  const scmIntegrationsApi = useApi(scmIntegrationsApiRef);
  const viewTechdocLink = useRouteRef(techdocsPlugin.routes.docRoot);

  const entitySourceLocation = getEntitySourceLocation(
    entity,
    scmIntegrationsApi,
  );
  const hasTechdocs =
    !!entity.metadata.annotations?.[TECHDOCS_ANNOTATION] ||
    !!entity.metadata.annotations?.[TECHDOCS_EXTERNAL_ANNOTATION];

  const integrationType = entitySourceLocation?.integrationType;
  const sourceLink: IconLinkVerticalProps = {
    label: 'View Source',
    disabled: !entitySourceLocation,
    icon: (
      <ScmIntegrationIcon
        {...(integrationType ? { type: integrationType } : {})}
      />
    ),
    ...(entitySourceLocation?.locationTargetUrl
      ? { href: entitySourceLocation.locationTargetUrl }
      : {}),
  };

  const techdocsHref = viewTechdocLink
    ? buildTechDocsURL(entity, viewTechdocLink)
    : undefined;
  const techdocsLink: IconLinkVerticalProps = {
    label: 'View TechDocs',
    disabled: !hasTechdocs || !viewTechdocLink,
    icon: <DocsIcon />,
    ...(techdocsHref ? { href: techdocsHref } : {}),
  };

  return <HeaderIconLinkRow links={[sourceLink, techdocsLink]} />;
}

/**
 * Drop-in replacement for `EntityAboutCard` on the component overview page
 * that adds the two Local Dev fields. Reuses Backstage's `AboutContent` for
 * the standard fields so they stay in sync with upstream, and reconstructs
 * the refresh / edit header actions and icon-link subheader.
 */
export function LocalDevAboutCard() {
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(alertApiRef);
  const errorApi = useApi(errorApiRef);
  const { allowed: canRefresh } = useEntityPermission(
    catalogEntityRefreshPermission,
  );

  const editUrl = entity.metadata.annotations?.[ANNOTATION_EDIT_URL];
  const location = entity.metadata.annotations?.[ANNOTATION_LOCATION];
  const allowRefresh =
    location?.startsWith('url:') || location?.startsWith('file:');

  const refreshEntity = useCallback(async () => {
    try {
      await catalogApi.refreshEntity(stringifyEntityRef(entity));
      alertApi.post({
        message: 'Refresh scheduled',
        severity: 'info',
        display: 'transient',
      });
    } catch (e) {
      errorApi.post(e as Error);
    }
  }, [catalogApi, entity, alertApi, errorApi]);

  return (
    <EntityInfoCard
      title="About"
      headerActions={
        <Fragment>
          {allowRefresh && canRefresh && (
            <IconButton
              aria-label="Refresh"
              title="Schedule entity refresh"
              onClick={refreshEntity}
            >
              <CachedIcon />
            </IconButton>
          )}
          <IconButton
            component={Link}
            aria-label="Edit"
            disabled={!editUrl}
            title="Edit Metadata"
            to={editUrl ?? '#'}
          >
            <EditIcon />
          </IconButton>
        </Fragment>
      }
    >
      <AboutSubheader />
      <AboutContent entity={entity} />
      <LocalDevFields />
    </EntityInfoCard>
  );
}
